import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import type { Enums } from "@/integrations/supabase/types";

/**
 * Version de chaque texte légal : la date de sa dernière mise à jour, en ISO.
 *
 * C'est la date affichée en haut de la page (« Dernière mise à jour ») et la
 * version enregistrée avec chaque acceptation (table `legal_acceptances`,
 * migration 20261103170000). Modifier un texte = changer sa date ici, puis
 * revoir `LEGAL_UPDATE` : le bandeau revient pour tous les comptes qui n'ont
 * pas la nouvelle version.
 */
export const LEGAL_VERSIONS = {
  cgv: "2026-11-03",
  cgu: "2026-11-03",
  confidentialite: "2026-11-03",
} as const;

type LegalDocument = keyof typeof LEGAL_VERSIONS;
export type LegalVersions = Partial<Record<LegalDocument, string>>;

/**
 * Passage aux versions ci-dessus pour les comptes ouverts avant leur publication.
 * - `effectiveDate` : date à laquelle elles s'appliquent au Client sans
 *   acceptation expresse, au moins 30 jours après l'e-mail de préavis
 *   (CGV art. 19). Proposée le 03/11/2026 : à confirmer avant l'envoi de l'e-mail.
 * - `previousCgv` : CGV en ligne jusque-là, consultables sur `/cgv/<version>` ;
 *   elles restent celles des Clients déjà inscrits jusqu'à leur acceptation, et
 *   au plus tard jusqu'à `effectiveDate`.
 *
 * Le bandeau des autres comptes s'appuie sur l'article 1 des CGU, publiées
 * pour la première fois : elles s'appliquent dès qu'ils continuent à utiliser
 * Equiply après en avoir été informés. Pour une révision, le préavis de
 * 30 jours de l'article 12 vaut aussi pour eux : revoir son texte.
 */
export const LEGAL_UPDATE = {
  effectiveDate: "2026-12-07",
  previousCgv: "2026-05-09",
} as const;

/** Le Client (gérant) contracte : il accepte CGV et CGU. Les autres comptes sont informés. */
export type LegalAudience = "client" | "user";

export interface LegalAcceptanceRecord {
  document: LegalDocument;
  version: string;
  action: "accepted" | "informed";
}

/** Textes à présenter, et s'il faut une acceptation (sinon, l'information suffit). */
const REQUIRED: Record<LegalAudience, ReadonlyArray<{ document: LegalDocument; mustAccept: boolean }>> = {
  client: [
    { document: "cgv", mustAccept: true },
    { document: "cgu", mustAccept: true },
    { document: "confidentialite", mustAccept: false },
  ],
  user: [
    { document: "cgu", mustAccept: false },
    { document: "confidentialite", mustAccept: false },
  ],
};

/** Gérant = Client ; Directeur, Adjoint et Employé = utilisateurs ; rien tant que le rôle est inconnu. */
export function legalAudienceFor(role: Enums<"app_role"> | null | undefined): LegalAudience | null {
  if (!role) return null;
  return role === "franchisee" ? "client" : "user";
}

/** Versions en cours des textes à présenter à ce public. */
export function legalDocumentsFor(audience: LegalAudience): LegalVersions {
  return Object.fromEntries(
    REQUIRED[audience].map(({ document }) => [document, LEGAL_VERSIONS[document]]),
  ) as LegalVersions;
}

/**
 * Le bandeau doit-il s'afficher ? Oui tant qu'un texte manque dans sa version
 * en cours : le Client doit avoir accepté CGV et CGU (« Plus tard » ne suffit
 * pas), les autres comptes en avoir été informés.
 */
export function needsLegalNotice(records: readonly LegalAcceptanceRecord[], audience: LegalAudience): boolean {
  return REQUIRED[audience].some(
    ({ document, mustAccept }) =>
      !records.some(
        (record) =>
          record.document === document &&
          record.version === LEGAL_VERSIONS[document] &&
          (!mustAccept || record.action === "accepted"),
      ),
  );
}

/** Date de la plus récente des versions présentées à ce public. */
export function latestLegalVersion(audience: LegalAudience): string {
  return REQUIRED[audience]
    .map(({ document }) => LEGAL_VERSIONS[document])
    .reduce((latest, version) => (version > latest ? version : latest));
}

/** Les nouvelles versions s'appliquent-elles déjà sans acceptation ? */
export function isLegalUpdateEffective(today: Date = new Date()): boolean {
  return format(today, "yyyy-MM-dd") >= LEGAL_UPDATE.effectiveDate;
}

/** « 3 novembre 2026 », « 1er décembre 2026 ». */
export function formatLegalDate(isoDate: string): string {
  const date = parseISO(isoDate);
  const label = format(date, "d MMMM yyyy", { locale: fr });
  return date.getDate() === 1 ? label.replace(/^1 /, "1er ") : label;
}
